import { fetchMFAPreference } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useGetAuthUser } from "@/api/get-user";
import { useLogout } from "@/api/logout";
import { LoadingScreen } from "@/components/loading-screen";
import { useTrackPageview } from "@/features/hubspot/components/track-pageview";
import { useNotificationStore } from "@/features/notifications/utils/notification-store";
import { axiosClient } from "@/lib/axios";
import { queryClient } from "@/lib/react-query";
import * as Sentry from "@sentry/react";

export function AuthenticatedGuard({
  children,
}: {
  children: React.ReactElement;
}) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { data: user, isSuccess, isLoading, isError, error } = useGetAuthUser();
  const { setUser } = useTrackPageview();
  const { mutateAsync: logout } = useLogout();
  const { reset: resetNotificationStore } = useNotificationStore();
  /**
   * Set the token in the axios client
   * This is needed for the API calls to work
   */
  useEffect(() => {
    const handleAuthState = async () => {
      if (isSuccess) {
        axiosClient.defaults.headers.common.Authorization = `Bearer ${user.token}`;
        if (user.email && user.isMarketing) {
          setUser(user.email);
          Sentry.setUser({
            email: user.email,
            id: user.id,
          });
        }
        setIsAuthenticated(true);
      } else if (isError) {
        // user token was revoked
        if (error.name === "NotAuthorizedException") {
          await logout();
        }
        setIsAuthenticated(false);
      }
    };

    handleAuthState();
  }, [isSuccess, user?.token, user, isError, setUser, error, logout]);

  /**
   * If the user is not authenticated, redirect to the sign-in page
   */
  useEffect(() => {
    if (!isSuccess && !isLoading) {
      navigate(`/sign-in?redirect=${encodeURIComponent(location.pathname)}`);
    }
  }, [isSuccess, isLoading, location.pathname, navigate]);

  /**
   * 2FA is required.
   * Redirect to 2FA setup if not enabled.
   */
  useEffect(() => {
    const verifyAuth = async () => {
      const mfaSettings = await fetchMFAPreference();
      if (!mfaSettings.enabled && location.pathname !== "/auth/setup-2fa") {
        navigate("/auth/setup-2fa");
        return;
      }
    };

    /**
     * You cannot call this function before the user is authenticated
     */
    if (isSuccess) {
      verifyAuth();
    }
  }, [isSuccess, navigate, location.pathname]);

  function listenAuthChange(): () => void {
    const listener = Hub.listen("auth", async (data) => {
      switch (data.payload.event) {
        case "signedOut":
          queryClient.clear();
          delete axiosClient.defaults.headers.common.Authorization;
          setIsAuthenticated(false);
          Sentry.setUser(null);
          resetNotificationStore();
          navigate("/sign-in");
          break;
      }
    });

    return listener;
  }

  useEffect(() => {
    const listener = listenAuthChange();

    return () => listener();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return <LoadingScreen />;
  }

  /**
   * User query just finished,
   * but token is not set yet
   */
  if (isSuccess && !isAuthenticated) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <LoadingScreen />;
  }

  return children;
}
